import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Search, 
  Calendar, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Building2,
  Users,
  CreditCard,
  Banknote,
  Percent,
  Download,
  Send,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  X,
  Filter
} from 'lucide-react';
import { settlementsServiceOptimized } from '../../firebase/settlementsServiceOptimized';
import { payoutService } from '../../firebase/payoutService';
import toast from 'react-hot-toast';
import withOptimization from '../../hoc/withOptimization';

const SettlementsOptimized = () => {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState('all');
  const [periodType, setPeriodType] = useState('monthly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [commissionPercentage, setCommissionPercentage] = useState(10);
  const [transferring, setTransferring] = useState(false);
  const [transferringId, setTransferringId] = useState(null);

  // Memoized filtered settlements for better performance
  const filteredSettlements = useMemo(() => {
    if (!searchTerm.trim()) return settlements;
    
    const term = searchTerm.toLowerCase().trim();
    return settlements.filter(settlement =>
      settlement.name?.toLowerCase().includes(term) ||
      settlement.email?.toLowerCase().includes(term) ||
      settlement.phone?.toLowerCase().includes(term) ||
      settlement.id?.toLowerCase().includes(term) ||
      settlement.coachingId?.toLowerCase().includes(term) ||
      settlement.userId?.toLowerCase().includes(term)
    );
  }, [searchTerm, settlements]);

  // Memoized totals calculation
  const totals = useMemo(() => {
    return filteredSettlements.reduce((acc, settlement) => {
      acc.totalCaptured += settlement.totalCaptured || 0;
      acc.totalRefunded += settlement.totalRefunded || 0;
      acc.totalFailed += settlement.totalFailed || 0;
      acc.totalNetAmount += settlement.netAmount || 0;
      acc.totalCommission += settlement.commissionAmount || 0;
      acc.totalSettlement += settlement.settlementAmount || 0;
      return acc;
    }, {
      totalCaptured: 0,
      totalRefunded: 0,
      totalFailed: 0,
      totalNetAmount: 0,
      totalCommission: 0,
      totalSettlement: 0
    });
  }, [filteredSettlements]);

  useEffect(() => {
    loadSettlements();
  }, [entityType, periodType, startDate, endDate, commissionPercentage]);

  const loadSettlements = useCallback(async () => {
    try {
      setLoading(true);
      let result;

      if (entityType === 'teachers' || entityType === 'all') {
        const teachersResult = await settlementsServiceOptimized.getTeachersSettlements(
          periodType,
          startDate || null,
          endDate || null,
          commissionPercentage
        );
        
        if (entityType === 'teachers') {
          result = teachersResult;
        } else {
          // Load both in parallel
          const institutesResult = await settlementsServiceOptimized.getInstitutesSettlements(
            periodType,
            startDate || null,
            endDate || null,
            commissionPercentage
          );
          
          const allSettlements = [
            ...(teachersResult.settlements || []).map(s => ({ ...s, type: 'teacher' })),
            ...(institutesResult.settlements || []).map(s => ({ ...s, type: 'institute' }))
          ];
          
          setSettlements(allSettlements);
          setLoading(false);
          return;
        }
      }

      if (entityType === 'institutes') {
        result = await settlementsServiceOptimized.getInstitutesSettlements(
          periodType,
          startDate || null,
          endDate || null,
          commissionPercentage
        );
      }

      if (result && result.success) {
        const settlementsWithType = (result.settlements || []).map(s => ({
          ...s,
          type: entityType === 'teachers' ? 'teacher' : 'institute'
        }));
        setSettlements(settlementsWithType);
      } else {
        toast.error('Failed to load settlements: ' + (result?.error || 'Unknown error'));
        setSettlements([]);
      }
    } catch (error) {
      toast.error('Error loading settlements: ' + error.message);
      setSettlements([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, periodType, startDate, endDate, commissionPercentage]);

  const handleMarkAsTransferred = useCallback(async (settlement) => {
    if (!window.confirm(`Mark settlement as transferred for ${settlement.name}?`)) {
      return;
    }

    try {
      const result = await settlementsServiceOptimized.updateSettlementTransferStatus(
        settlement.type,
        settlement.id,
        settlement.id,
        'transferred',
        { transferredAt: new Date(), transferredBy: 'admin' }
      );

      if (result.success) {
        toast.success('Settlement marked as transferred');
        loadSettlements();
      } else {
        toast.error('Failed to update transfer status');
      }
    } catch (error) {
      toast.error('Error updating transfer status: ' + error.message);
    }
  }, [loadSettlements]);

  const handleAutomaticTransfer = useCallback(async (settlement) => {
    // Validate bank details
    if (!settlement.bankDetails) {
      toast.error('Bank account details not available. Please ask the entity to update their bank details.');
      return;
    }

    const { accountNumber, ifscCode, accountHolderName, bankName } = settlement.bankDetails;

    if (!accountNumber || !ifscCode || !accountHolderName) {
      toast.error('Incomplete bank account details. Account number, IFSC code, and account holder name are required.');
      return;
    }

    // Validate amount
    if (!settlement.settlementAmount || settlement.settlementAmount <= 0) {
      toast.error('Invalid settlement amount');
      return;
    }

    // Confirm transfer
    if (!window.confirm(
      `Transfer ₹${formatCurrency(settlement.settlementAmount)} to ${settlement.name}?\n\n` +
      `Account: ${accountNumber}\n` +
      `IFSC: ${ifscCode}\n` +
      `Bank: ${bankName || 'N/A'}`
    )) {
      return;
    }

    setTransferring(true);
    setTransferringId(settlement.id);

    try {
      const payoutData = {
        accountNumber: accountNumber,
        ifscCode: ifscCode,
        accountHolderName: accountHolderName,
        amount: settlement.settlementAmount,
        currency: 'INR',
        mode: 'NEFT',
        purpose: 'settlement',
        entityType: settlement.type,
        entityId: settlement.id,
        entityName: settlement.name,
        settlementId: settlement.id,
        period: getPeriodLabel(),
        referenceId: `settlement_${settlement.type}_${settlement.id}_${Date.now()}`
      };

      const result = await payoutService.createPayout(payoutData);

      if (result.success) {
        toast.success(`Transfer initiated successfully! Payout ID: ${result.payout.id}`);
        
        // Update settlement status
        await settlementsServiceOptimized.updateSettlementTransferStatus(
          settlement.type,
          settlement.id,
          settlement.id,
          'transferred',
          {
            transferredAt: new Date(),
            transferredBy: 'automatic',
            payoutId: result.payout.id,
            razorpayPayoutId: result.payout.id,
            utr: result.payout.utr,
            status: result.payout.status
          }
        );

        loadSettlements();
      } else {
        toast.error('Failed to initiate transfer: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error initiating transfer: ' + error.message);
    } finally {
      setTransferring(false);
      setTransferringId(null);
    }
  }, [loadSettlements]);

  const handleBulkTransfer = useCallback(async () => {
    const settlementsToTransfer = filteredSettlements.filter(s => 
      s.settlementAmount > 0 && 
      s.bankDetails && 
      s.bankDetails.accountNumber && 
      s.bankDetails.ifscCode &&
      s.bankDetails.accountHolderName
    );

    if (settlementsToTransfer.length === 0) {
      toast.error('No settlements available for automatic transfer. Check bank details.');
      return;
    }

    const totalAmount = settlementsToTransfer.reduce((sum, s) => sum + s.settlementAmount, 0);
    
    if (!window.confirm(
      `Transfer settlements to ${settlementsToTransfer.length} entities?\n\n` +
      `Total Amount: ${formatCurrency(totalAmount)}\n\n` +
      `This will initiate ${settlementsToTransfer.length} separate transfers.`
    )) {
      return;
    }

    setTransferring(true);

    try {
      let successCount = 0;
      let failCount = 0;

      for (const settlement of settlementsToTransfer) {
        try {
          const payoutData = {
            accountNumber: settlement.bankDetails.accountNumber,
            ifscCode: settlement.bankDetails.ifscCode,
            accountHolderName: settlement.bankDetails.accountHolderName,
            amount: settlement.settlementAmount,
            currency: 'INR',
            mode: 'NEFT',
            purpose: 'settlement',
            entityType: settlement.type,
            entityId: settlement.id,
            entityName: settlement.name,
            settlementId: settlement.id,
            period: getPeriodLabel(),
            referenceId: `settlement_${settlement.type}_${settlement.id}_${Date.now()}`
          };

          const result = await payoutService.createPayout(payoutData);

          if (result.success) {
            successCount++;
            await settlementsServiceOptimized.updateSettlementTransferStatus(
              settlement.type,
              settlement.id,
              settlement.id,
              'transferred',
              {
                transferredAt: new Date(),
                transferredBy: 'automatic',
                payoutId: result.payout.id,
                razorpayPayoutId: result.payout.id,
                utr: result.payout.utr,
                status: result.payout.status
              }
            );
          } else {
            failCount++;
          }

          // Small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (error) {
          failCount++;
          console.error(`Failed to transfer for ${settlement.name}:`, error);
        }
      }

      toast.success(`Bulk transfer completed: ${successCount} successful, ${failCount} failed`);
      loadSettlements();
    } catch (error) {
      toast.error('Error during bulk transfer: ' + error.message);
    } finally {
      setTransferring(false);
    }
  }, [filteredSettlements, loadSettlements]);

  // Utility functions
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  }, []);

  const formatDate = useCallback((date) => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return date.toDate().toLocaleDateString('en-IN');
    }
    return new Date(date).toLocaleDateString('en-IN');
  }, []);

  const getPeriodLabel = useCallback(() => {
    switch (periodType) {
      case 'monthly':
        return 'This Month';
      case 'yearly':
        return 'This Year';
      case 'custom':
        return `${formatDate(startDate)} - ${formatDate(endDate)}`;
      default:
        return 'This Month';
    }
  }, [periodType, startDate, endDate, formatDate]);

  const exportSettlements = useCallback(() => {
    const csvContent = [
      ['Entity Type', 'Name', 'Email', 'Phone', 'Total Captured', 'Total Refunded', 'Total Failed', 'Net Amount', 'Commission %', 'Commission Amount', 'Settlement Amount', 'Transaction Count'],
      ...filteredSettlements.map(s => [
        s.type === 'teacher' ? 'Teacher' : 'Institute',
        s.name || '',
        s.email || '',
        s.phone || '',
        s.totalCaptured || 0,
        s.totalRefunded || 0,
        s.totalFailed || 0,
        s.netAmount || 0,
        s.commissionPercentage || 0,
        s.commissionAmount || 0,
        s.settlementAmount || 0,
        s.transactionCount || 0
      ]),
      ['', '', '', 'TOTALS', totals.totalCaptured, totals.totalRefunded, totals.totalFailed, totals.totalNetAmount, '', totals.totalCommission, totals.totalSettlement, '']
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `settlements_${periodType}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [filteredSettlements, totals, periodType]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settlements Management</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Track and manage settlements for institutes and tuition teachers
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Entity Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Entity Type
            </label>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All</option>
              <option value="teachers">Teachers</option>
              <option value="institutes">Institutes</option>
            </select>
          </div>

          {/* Period Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Period
            </label>
            <select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
              <option value="custom">Custom Date Range</option>
            </select>
          </div>

          {/* Date Range (shown when custom) */}
          {periodType === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </>
          )}

          {/* Commission Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Commission % ({commissionPercentage}%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={commissionPercentage}
              onChange={(e) => setCommissionPercentage(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name, email, phone, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={loadSettlements}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={exportSettlements}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={handleBulkTransfer}
            disabled={transferring || filteredSettlements.length === 0}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            {transferring ? 'Transferring...' : 'Bulk Auto Transfer'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Captured</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(totals.totalCaptured)}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Refunded</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(totals.totalRefunded)}
              </p>
            </div>
            <TrendingDown className="w-8 h-8 text-red-600" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Failed</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(totals.totalFailed)}
              </p>
            </div>
            <XCircle className="w-8 h-8 text-orange-600" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Net Amount</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(totals.totalNetAmount)}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Commission</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(totals.totalCommission)}
              </p>
            </div>
            <Percent className="w-8 h-8 text-yellow-600" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Settlement</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(totals.totalSettlement)}
              </p>
            </div>
            <Banknote className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Settlements Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        {filteredSettlements.length === 0 ? (
          <div className="p-12 text-center">
            <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">No settlements found</p>
            <p className="text-gray-500 dark:text-gray-500 text-sm">
              {searchTerm ? 'Try adjusting your search terms' : `No settlements for ${getPeriodLabel()}`}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Entity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Captured</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Refunded</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Failed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Net Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Settlement</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Transactions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredSettlements.map((settlement) => (
                  <tr key={settlement.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {settlement.name || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {settlement.email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        settlement.type === 'teacher' 
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                      }`}>
                        {settlement.type === 'teacher' ? 'Teacher' : 'Institute'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 dark:text-green-400">
                      {formatCurrency(settlement.totalCaptured)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600 dark:text-red-400">
                      {formatCurrency(settlement.totalRefunded)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-orange-600 dark:text-orange-400">
                      {formatCurrency(settlement.totalFailed || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(settlement.netAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {settlement.commissionPercentage}% ({formatCurrency(settlement.commissionAmount)})
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-purple-600 dark:text-purple-400">
                      {formatCurrency(settlement.settlementAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {settlement.transactionCount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedSettlement(settlement);
                            setShowDetails(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        {settlement.bankDetails && settlement.bankDetails.accountNumber && (
                          <button
                            onClick={() => handleAutomaticTransfer(settlement)}
                            disabled={transferring && transferringId === settlement.id}
                            className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 disabled:opacity-50"
                            title="Auto Transfer to Bank"
                          >
                            {transferring && transferringId === settlement.id ? (
                              <Clock className="w-5 h-5 animate-spin" />
                            ) : (
                              <Send className="w-5 h-5" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleMarkAsTransferred(settlement)}
                          className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                          title="Mark as Transferred (Manual)"
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Settlement Details Modal */}
      {showDetails && selectedSettlement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Settlement Details</h2>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedSettlement(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Entity Information */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    {selectedSettlement.type === 'teacher' ? <Users className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
                    {selectedSettlement.type === 'teacher' ? 'Teacher' : 'Institute'} Information
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Name:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {selectedSettlement.name || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Email:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">
                        {selectedSettlement.email || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">
                        {selectedSettlement.phone || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">ID:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-mono text-xs">
                        {selectedSettlement.id || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Settlement Summary */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Settlement Summary</h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Period:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {getPeriodLabel()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Total Captured:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium text-green-600">
                        {formatCurrency(selectedSettlement.totalCaptured)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Total Refunded:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium text-red-600">
                        {formatCurrency(selectedSettlement.totalRefunded)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Total Failed:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium text-orange-600">
                        {formatCurrency(selectedSettlement.totalFailed || 0)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Net Amount:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {formatCurrency(selectedSettlement.netAmount)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Commission ({selectedSettlement.commissionPercentage}%):</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium text-yellow-600">
                        {formatCurrency(selectedSettlement.commissionAmount)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Settlement Amount:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-bold text-purple-600 text-lg">
                        {formatCurrency(selectedSettlement.settlementAmount)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bank Account Details */}
                {selectedSettlement.bankDetails ? (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Bank Account Details</h3>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Account Holder Name:</span>
                        <span className="ml-2 text-gray-900 dark:text-white font-medium">
                          {selectedSettlement.bankDetails.accountHolderName || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Account Number:</span>
                        <span className="ml-2 text-gray-900 dark:text-white font-mono">
                          {selectedSettlement.bankDetails.accountNumber || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">IFSC Code:</span>
                        <span className="ml-2 text-gray-900 dark:text-white font-mono">
                          {selectedSettlement.bankDetails.ifscCode || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Bank Name:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">
                          {selectedSettlement.bankDetails.bankName || 'N/A'}
                        </span>
                      </div>
                      {selectedSettlement.bankDetails.branchName && (
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Branch Name:</span>
                          <span className="ml-2 text-gray-900 dark:text-white">
                            {selectedSettlement.bankDetails.branchName}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      ⚠️ Bank account details not available. Please ask the {selectedSettlement.type === 'teacher' ? 'teacher' : 'institute'} to update their bank details.
                    </p>
                  </div>
                )}

                {/* Transactions List */}
                {selectedSettlement.transactions && selectedSettlement.transactions.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                      Transactions ({selectedSettlement.transactions.length})
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-gray-600">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Date</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Amount</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Customer</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                          {selectedSettlement.transactions.map((transaction, idx) => (
                            <tr key={idx}>
                              <td className="px-3 py-2 text-gray-900 dark:text-white">
                                {formatDate(transaction.date)}
                              </td>
                              <td className="px-3 py-2 font-medium text-green-600 dark:text-green-400">
                                {formatCurrency(transaction.amount)}
                              </td>
                              <td className="px-3 py-2 text-gray-900 dark:text-white">
                                {transaction.studentName || transaction.customerName || transaction.description || 'N/A'}
                                {transaction.type === 'referral' && (
                                  <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">(Referral)</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  transaction.type === 'referral' 
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    : transaction.status === 'captured' || transaction.status === 'referral_bonus'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                }`}>
                                  {transaction.status || 'completed'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t">
                  {selectedSettlement.bankDetails && selectedSettlement.bankDetails.accountNumber && (
                    <button
                      onClick={() => handleAutomaticTransfer(selectedSettlement)}
                      disabled={transferring && transferringId === selectedSettlement.id}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
                    >
                      {transferring && transferringId === selectedSettlement.id ? (
                        <>
                          <Clock className="w-4 h-4 animate-spin" />
                          Transferring...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Auto Transfer to Bank
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => handleMarkAsTransferred(selectedSettlement)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Mark as Transferred (Manual)
                  </button>
                  <button
                    onClick={() => {
                      setShowDetails(false);
                      setSelectedSettlement(null);
                    }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default withOptimization(SettlementsOptimized, {
  name: 'SettlementsOptimized',
  enableVirtualization: false,
  enableMemoization: true
});

